import { API_BASE } from '../lib/apiOrigin';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import { useReveal } from "../hooks/useReveal";

const API = API_BASE;

// Public project evidence comes only from the configured gallery API.
const EMPTY_COPY = {
  en: { heading: "Projects", loading: "Loading project photos…", empty: "No project photos are published here yet.", unavailable: "Project photos are temporarily unavailable." },
  pt: { heading: "Projetos", loading: "Carregando fotos de projetos…", empty: "Ainda não há fotos de projetos publicadas aqui.", unavailable: "As fotos de projetos estão temporariamente indisponíveis." },
  es: { heading: "Proyectos", loading: "Cargando fotos de proyectos…", empty: "Aún no hay fotos de proyectos publicadas aquí.", unavailable: "Las fotos de proyectos no están disponibles temporalmente." },
};

const GalleryItem = ({ p, i }) => {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      data-testid={`project-${i}`}
      style={{ transitionDelay: `${(i % 3) * 90}ms` }}
      className="reveal group relative overflow-hidden border border-white/5 hover:border-sky-400/40 transition-colors duration-500"
    >
      {/* Fixed-aspect image container — uniform card heights */}
      <div className="xa-media-dark relative aspect-[4/5] overflow-hidden bg-zinc-950">
        <img
          src={p.image_url}
          alt={p.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent"></div>

        {p.tag && (
          <div className="absolute top-4 left-4 bg-sky-500 text-white text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 transition-transform duration-500 group-hover:-translate-y-0.5">
            {p.tag}
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
          <h4 className="text-white font-display font-bold text-lg sm:text-xl leading-tight">
            {p.title}
          </h4>
          {p.location && (
            <p className="text-zinc-300 text-xs mt-1.5 opacity-80 group-hover:opacity-100 transition-opacity">
              {p.location}
            </p>
          )}
          {p.caption && (
            <p className="text-zinc-200 text-sm mt-2.5 leading-snug max-h-0 group-hover:max-h-32 overflow-hidden opacity-0 group-hover:opacity-100 transition-all duration-500">
              {p.caption}
            </p>
          )}
          <div className="mt-3 h-px w-0 group-hover:w-10 bg-sky-400 transition-all duration-500"></div>
        </div>
      </div>
    </div>
  );
};

const Gallery = () => {
  const { t, lang } = useLang();
  const [projects, setProjects] = useState([]);
  const [loadStatus, setLoadStatus] = useState("loading");
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");

  const loadProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setProjects(Array.isArray(res.data) ? res.data : []);
      setLoadStatus(Array.isArray(res.data) ? "ready" : "error");
    } catch (err) {
      setLoadStatus("error");
      if (process.env.NODE_ENV === "development") {
        console.error("[Gallery] failed to load gallery, leaving gallery empty:", err);
      }
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gallery-categories`);
      const cats = Array.isArray(res.data?.categories) ? res.data.categories : [];
      setCategories(cats);
    } catch (err) {
      // no-op — filter bar just won't show
    }
  }, []);

  useEffect(() => {
    loadProjects();
    loadCategories();
  }, [loadProjects, loadCategories]);

  // Only render category chips that actually match at least one photo — a
  // filter that returns zero cards is worse UX than hiding the chip.
  const visibleCategories = useMemo(() => {
    const tagsInUse = new Set(projects.map((p) => p.tag).filter(Boolean));
    return categories.filter((c) => tagsInUse.has(c));
  }, [categories, projects]);

  const filtered = useMemo(() => {
    if (activeCat === "all") return projects;
    return projects.filter((p) => p.tag === activeCat);
  }, [projects, activeCat]);

  const emptyCopy = EMPTY_COPY[lang] || EMPTY_COPY.en;
  if (!projects.length) return (
    <section id="projects" aria-labelledby="projects-heading" data-testid="projects-empty" className="py-16 sm:py-24 px-5 sm:px-6 bg-[#09090b] text-center">
      <div className="mx-auto max-w-3xl">
        <h2 id="projects-heading" className="font-display font-black text-white text-3xl sm:text-5xl tracking-tight">{emptyCopy.heading}</h2>
        <p role="status" className="mt-6 text-zinc-300 text-base sm:text-lg">{loadStatus === "loading" ? emptyCopy.loading : loadStatus === "error" ? emptyCopy.unavailable : emptyCopy.empty}</p>
      </div>
    </section>
  );

  return (
    <section
      id="projects"
      aria-labelledby="projects-heading"
      data-testid="projects-section"
      className="relative py-16 sm:py-24 lg:py-32 bg-[#09090b]"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-7 space-y-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-sky-400"></span>
              <span className="eyebrow">{t.projects.eyebrow}</span>
            </div>
            <h2 id="projects-heading" className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
              {t.projects.title}
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-zinc-400 text-base sm:text-lg leading-relaxed">
              {t.projects.subtitle}
            </p>
          </div>
        </div>

        {visibleCategories.length > 0 && (
          <div
            data-testid="gallery-filter-bar"
            className="flex flex-wrap gap-2 mb-8 sm:mb-10"
          >
            <button
              type="button"
              onClick={() => setActiveCat("all")}
              data-testid="gallery-filter-all"
              className={`text-xs uppercase tracking-[0.2em] font-bold px-4 py-2 border transition-colors ${
                activeCat === "all"
                  ? "border-sky-400 text-white bg-sky-500/10"
                  : "border-white/15 text-zinc-300 hover:border-sky-400 hover:text-white"
              }`}
            >
              All
            </button>
            {visibleCategories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActiveCat(c)}
                data-testid={`gallery-filter-${c}`}
                className={`text-xs uppercase tracking-[0.2em] font-bold px-4 py-2 border transition-colors ${
                  activeCat === c
                    ? "border-sky-400 text-white bg-sky-500/10"
                    : "border-white/15 text-zinc-300 hover:border-sky-400 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {filtered.map((p, i) => (
            <GalleryItem key={p.id || i} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Gallery;
